import type { FC, ReactNode, RefObject } from 'react'
import type { ObjectVariant } from '@/components/landing/object-scene'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useScroll, useSpring, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import JarRender from '@/components/landing/jar-render'
import Sparkle from '@/components/landing/sparkle'
import DragHint from '@/components/generals/drag-hint'

// WebGL only exists in the browser, so the 3D piece loads on the client.
const ObjectScene = dynamic(() => import('@/components/landing/object-scene'), { ssr: false })

type SceneState = 'loading' | 'ready' | 'failed'

interface HeroObjectProps {
    // The section whose scroll drives the motion.
    sectionRef: RefObject<HTMLElement | null>
    variant: ObjectVariant
    // A card tint utility for the disc behind it, for example bg-sage.
    tint: string
    label: string
}

// A page header's own 3D piece, in the same soft light and motion as the home tube:
// it floats, leans toward the cursor and moves with the scroll (the jar opens, the stick twists up, the drop turns).
// Visitors can grab it and turn it by hand; it eases back to the front when let go.
const HeroObject: FC<HeroObjectProps> = ({ sectionRef, variant, tint, label }): ReactNode => {
    const [scene, setScene] = useState<SceneState>('loading')
    const [grabbed, setGrabbed] = useState(false)

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })
    const scroll = useTransform(scrollYProgress, latest => Math.min(Math.max(latest / 0.8, 0), 1))
    const progress = useSpring(scroll, { stiffness: 140, damping: 32, mass: 1, restDelta: 0.0005 })

    return (
        <div role='img' aria-label={label} className='relative mx-auto aspect-square w-[min(82vw,30rem)]'>
            <div aria-hidden='true' className={cn('absolute inset-[7%] rounded-full', tint)} />

            <Sparkle className='absolute left-[6%] top-[9%] size-4 text-white motion-safe:animate-softly-twinkle' />
            <Sparkle className='absolute bottom-[10%] right-[5%] size-3 text-cocoa/40 motion-safe:animate-softly-twinkle [animation-delay:1.8s]' />

            {scene !== 'failed' ? (
                <ObjectScene
                    variant={variant}
                    progress={progress}
                    onReady={() => setScene('ready')}
                    onError={() => setScene('failed')}
                    onGrab={() => setGrabbed(true)}
                    className={cn('z-10 transition-opacity duration-1000 ease-out', scene === 'ready' ? 'opacity-100' : 'opacity-0')}
                />
            ) : variant === 'jar' && (
                <div aria-hidden='true' className='absolute inset-[22%]'>
                    <JarRender lid />
                </div>
            )}

            <DragHint show={scene === 'ready' && !grabbed} />
        </div>
    )
}

export default HeroObject
